import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Box, Divider, IconButton, Tooltip } from '@mui/material';
import { DeleteIcon } from '@sharedCR/coreDesignSystem/icons';
import ModalFormWrapper from '@sharedCR/coreDesignSystem/ModalFormWrapper';
import {
  FormVariant,
  formVariantsOptions,
} from 'app/pages/loanApplication/tabs/tasks/Tasks.constants';
import { FormViewer } from 'app/pages/system/forms';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import { onAutocompleteChangeInForm } from 'app/shared/components/form/Autocomplete';
import { AutocompleteCDS, TextFieldCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import CreateFormModal from 'app/shared/modules/CreateFormModal';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { compose } from 'redux';

import { FormViewerNavigationType, LoxDocumentTypeName } from 'constants/forms';
import { NATIONAL_CATEGORY_CODE } from 'constants/states';
import useForm from 'hooks/useForm';

import { getLoanSpecificForms, setFormNavigationTypeAction } from 'store/forms/actions';
import { closeDialogAction, openDialogAction, showMessageAction } from 'store/global/actions';
import {
  addLoanConditionsAction,
  getLoanConditionByLoanIdAction,
  getLoanApplicationTasksAction,
} from 'store/loanApplications/actions';
import { selectFormLockedFieldsMap, selectFormLockedPage } from 'store/loanApplications/selectors';
import { selectTaskTypeDynamicField } from 'store/system/selectors';
import {
  selectConditionSetsNotEmpty,
  selectConditionsWithNumberName,
  selectConditionsWithTaskTypes,
  selectPriorTo,
} from 'store/underwriting/selectors';

import { UnderwritingTestIds } from '../../Underwriting.testIds';

import AddConditionFormTaskType from './AddConditionFormTaskType';
import {
  getConditionsTaskType,
  getSchema,
  prepareDataBeforeRequest,
} from './AddConditionsForm.utils';

const lineClamp3Styles = {
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
};

const conditionSetInfoContainerStyles = {
  '& .autocomplete-wrapper': {
    width: '30%',
  },

  '& .description': {
    width: '100%',
  },
};

const initialFormValues = {
  conditions: [],
  conditionSets: [],
  conditionTaskTypes: {},
};

const autocompleteInputProps = { margin: 'none' };
const disablePortalProp = { disablePortal: true };
const tooltipClasses = { tooltip: 'max-w-640 text-12' };
const listBoxProps = {
  style: {
    maxHeight: '45vh',
  },
};

const AddConditionsForm = ({
  formRef,
  setIsLoading,
  loanId,
  isLoading,
  initialForm,
  customOnAssignFormClick = _.noop,
}) => {
  const dispatch = useDispatch();

  const { applicationId } = useParams();

  const conditionSetsOptions = useSelector(selectConditionSetsNotEmpty);
  const conditionsOptions = useSelector(selectConditionsWithNumberName);
  const conditions = useSelector(selectConditionsWithTaskTypes);
  const taskTypeDynamicFieldOptions = useSelector(selectTaskTypeDynamicField);
  const priorToOptions = useSelector(selectPriorTo);
  const lockedFieldsMap = useSelector(
    selectFormLockedFieldsMap(FormNames.ADD_CONDITIONS_AND_CONDITIONS_SETS),
  );
  const isPageLocked = useSelector(
    selectFormLockedPage(FormNames.ADD_CONDITIONS_AND_CONDITIONS_SETS),
  );

  const isDisabledByLock = isLoading || isPageLocked;

  const {
    form,
    isFormChanged,
    setInForm,
    setMultipleInForm,
    handleChange,
    setSchema,
    validateField,
    validationErrors,
    validateForm,
  } = useForm(initialForm || initialFormValues);

  useEffect(() => {
    setSchema(getSchema({}));
  }, [setSchema]);

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      if (!(await validateForm())) {
        return;
      }

      setIsLoading(true);

      const dataToSend = prepareDataBeforeRequest({
        form,
        loanId,
        taskTypeDynamicFieldOptions,
      });

      dispatch(addLoanConditionsAction(dataToSend))
        .then(() =>
          Promise.all([
            dispatch(
              getLoanConditionByLoanIdAction({ loanId, partialLoad: true, withDeleted: true }),
            ),
            dispatch(getLoanApplicationTasksAction()),
          ]),
        )
        .then(() => {
          dispatch(
            showMessageAction({ message: 'Condition(s) successfully added.', variant: 'success' }),
          );
          dispatch(closeDialogAction());
        })
        .catch(Error)
        .finally(() => setIsLoading(false));
    },
    [validateForm, setIsLoading, form, loanId, taskTypeDynamicFieldOptions, dispatch],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onAutocompleteChange = useCallback(
    onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  useEffect(() => {
    const updatedConditionTypes = getConditionsTaskType({
      form,
      detailedConditions: conditions,
      taskTypeDynamicFieldOptions,
      priorToOptions,
    });
    setInForm('conditionTaskTypes', updatedConditionTypes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.conditions, form.conditionSets, setInForm, conditions, priorToOptions]);

  useEffect(() => {
    setSchema(
      getSchema({ conditionTaskTypes: form.conditionTaskTypes, taskTypeDynamicFieldOptions }),
    );
  }, [form.conditionTaskTypes, setSchema, taskTypeDynamicFieldOptions]);

  const onConditionDelete = useCallback(
    e => {
      const { id } = e.currentTarget;

      const updatedConditionTaskTypes = _.clone(form.conditionTaskTypes);
      const updatedConditionSets = _.clone(form.conditionSets)
        .map(({ links, ...conditionSet }) => ({
          ...conditionSet,
          links: links.filter(({ conditionId }) => conditionId !== id),
        }))
        .filter(({ links }) => !_.isEmpty(links));

      delete updatedConditionTaskTypes[id];

      setMultipleInForm({
        conditionTaskTypes: updatedConditionTaskTypes,
        conditions: form.conditions.filter(condition => condition.id !== id),
        conditionSets: updatedConditionSets,
      });
    },
    [form.conditionSets, form.conditionTaskTypes, form.conditions, setMultipleInForm],
  );

  const onCreateNewForm = useCallback(
    ({ conditionId, taskTypeId }) => {
      const abortController = new AbortController();

      const onFinishFormUploading = uploadedForm => {
        dispatch(getLoanSpecificForms({ loanId, applicationId }))
          .catch(Error)
          .finally(() => {
            const formParams = {
              name: uploadedForm.name,
              formId: uploadedForm.formId,
              historyEntityId: undefined,
              formType: uploadedForm.formType,
              selectedPage: 1,
            };

            const newForm = _.cloneDeep(form);

            newForm.conditionTaskTypes[conditionId].conditionTaskTypes[taskTypeId].formVariant =
              formVariantsOptions.find(
                formVariant => formVariant.value === FormVariant.LOAN_SPECIFIC,
              );
            newForm.conditionTaskTypes[conditionId].conditionTaskTypes[taskTypeId].formId =
              uploadedForm.formId;

            const onAssignClick = () => customOnAssignFormClick(newForm);

            dispatch(
              openDialogAction({
                header: 'Create new loan specific form',
                body: dialogProps => (
                  <FormViewer
                    isDisableResetStateAction
                    isNeedCloseButton
                    isShowAssignFormIconButton
                    customOnAssignFormClick={onAssignClick}
                    formParams={formParams}
                    {...dialogProps}
                  />
                ),
                disableEnforceFocus: true,
              }),
            );
          });
      };

      dispatch(
        openDialogAction({
          header: 'Create new loan specific form',
          body: dialogProps => (
            <CreateFormModal
              isDisableResetStateAction
              isLoanSpecificForm
              isPdfOnly
              abortController={abortController}
              applicationId={applicationId}
              defaultDocumentTypeName={LoxDocumentTypeName}
              formAreaCode={NATIONAL_CATEGORY_CODE}
              onFinishFormUploading={onFinishFormUploading}
              {...dialogProps}
            />
          ),
          afterCloseCallback: () => abortController.abort(),
        }),
      );
    },
    [applicationId, customOnAssignFormClick, dispatch, form, loanId],
  );

  const preparedConditionTaskTypes = useMemo(
    () =>
      _.sortBy(Object.values(form.conditionTaskTypes), [
        ({ priorToType }) => priorToType?.order,
        'number',
      ]),
    [form.conditionTaskTypes],
  );

  useEffect(() => {
    dispatch(setFormNavigationTypeAction(FormViewerNavigationType.STATE));
  }, [dispatch]);

  const renderOption = useCallback(({ optionText }) => {
    return (
      <Tooltip PopperProps={disablePortalProp} classes={tooltipClasses} title={optionText}>
        <div className="break-all" style={lineClamp3Styles}>
          {optionText}
        </div>
      </Tooltip>
    );
  }, []);

  return (
    <ModalFormWrapper
      formRef={formRef}
      formTestId="add-conditions"
      isFormChanged={isFormChanged}
      isSubmitting={isLoading}
      submitButtonName="Add"
      onSubmit={onSubmit}
    >
      <AutocompleteCDS
        disableCloseOnSelect
        disablePortal
        multiple
        withTooltip
        ListboxProps={listBoxProps}
        OptionContent={renderOption}
        containerClassName="mb-24"
        dataTestIdInput={UnderwritingTestIds.ADD_BROKER_CONDITION_FORM_CONDITIONS_SELECT}
        dataTestIdOption={UnderwritingTestIds.ADD_BROKER_CONDITION_FORM_CONDITIONS_OPTION}
        disabled={isDisabledByLock || lockedFieldsMap.conditions}
        error={!!validationErrors.conditions}
        helperText={validationErrors.conditions}
        label="Conditions"
        name="conditions"
        options={conditionsOptions}
        value={form.conditions}
        onChange={onAutocompleteChange}
      />

      <AutocompleteCDS
        disableCloseOnSelect
        disablePortal
        multiple
        containerClassName="mb-24"
        dataTestIdInput={UnderwritingTestIds.ADD_BROKER_CONDITION_FORM_CONDITION_SETS_SELECT}
        dataTestIdOption={UnderwritingTestIds.ADD_BROKER_CONDITION_FORM_CONDITION_SETS_OPTION}
        disabled={isDisabledByLock || lockedFieldsMap.conditionSets}
        error={!!validationErrors.conditionSets}
        helperText={validationErrors.conditionSets}
        label="Condition sets"
        name="conditionSets"
        options={conditionSetsOptions}
        value={form.conditionSets}
        onChange={onAutocompleteChange}
      />

      {preparedConditionTaskTypes.map(
        ({
          conditionId,
          message,
          number,
          priorToType,
          conditionTaskTypes,
          borrowerMessage,
          subCompanyMessage,
        }) => (
          <Box key={conditionId} sx={conditionSetInfoContainerStyles}>
            <Divider />
            <div className="flex gap-24 items-center mb-24 mt-24">
              <AutocompleteCDS
                disableClearable
                required
                dataTestIdInput={UnderwritingTestIds.ADD_BROKER_CONDITION_FORM_PRIOR_TO_SELECT}
                dataTestIdOption={UnderwritingTestIds.ADD_BROKER_CONDITION_FORM_PRIOR_TO_OPTION}
                disabled={isDisabledByLock || lockedFieldsMap.priorToType}
                error={!!validationErrors.conditionTaskTypes?.[conditionId]?.priorToType}
                helperText={validationErrors.conditionTaskTypes?.[conditionId]?.priorToType}
                inputProps={autocompleteInputProps}
                label="Prior to"
                name={`conditionTaskTypes.${conditionId}.priorToType`}
                options={priorToOptions}
                value={priorToType}
                withSortedOptions={false}
                onChange={onAutocompleteChange}
              />
              <TextFieldCDS
                fullWidth
                multiline
                required
                dataTestId={UnderwritingTestIds.getConditionMessageInputId(conditionId)}
                disabled={isDisabledByLock || lockedFieldsMap.message}
                error={!!validationErrors.conditionTaskTypes?.[conditionId]?.message}
                helperText={validationErrors.conditionTaskTypes?.[conditionId]?.message}
                label="Condition"
                maxLength={1000}
                name={`conditionTaskTypes.${conditionId}.message`}
                type="text"
                value={message}
                variant="outlined"
                onChange={handleChange}
              />
              <TextFieldCDS
                disabled
                dataTestId={UnderwritingTestIds.getConditionNumberInputId(conditionId)}
                label="Condition number"
                name={`conditionTaskTypes.${conditionId}.number`}
                type="text"
                value={number}
                variant="outlined"
                onChange={handleChange}
              />
              <IconButton
                aria-label="delete"
                data-testid={UnderwritingTestIds.getConditionDeleteButtonId(conditionId)}
                id={conditionId}
                onClick={onConditionDelete}
              >
                <DeleteIcon />
              </IconButton>
            </div>

            <TextFieldCDS
              fullWidth
              multiline
              className="mb-24"
              dataTestId={UnderwritingTestIds.getConditionBorrowerMessageInputId(conditionId)}
              disabled={isDisabledByLock || lockedFieldsMap.borrowerMessage}
              error={!!validationErrors.conditionTaskTypes?.[conditionId]?.borrowerMessage}
              helperText={validationErrors.conditionTaskTypes?.[conditionId]?.borrowerMessage}
              label="Borrower Facing Condition"
              maxLength={1000}
              name={`conditionTaskTypes.${conditionId}.borrowerMessage`}
              type="text"
              value={borrowerMessage}
              variant="outlined"
              onChange={handleChange}
            />

            <TextFieldCDS
              fullWidth
              multiline
              className="mb-24"
              dataTestId={UnderwritingTestIds.getConditionSubCompanyMessageInputId(conditionId)}
              disabled={isDisabledByLock || lockedFieldsMap.subCompanyMessage}
              error={!!validationErrors.conditionTaskTypes?.[conditionId]?.subCompanyMessage}
              helperText={validationErrors.conditionTaskTypes?.[conditionId]?.subCompanyMessage}
              label="Sub Company Facing Condition"
              maxLength={1000}
              name={`conditionTaskTypes.${conditionId}.subCompanyMessage`}
              type="text"
              value={subCompanyMessage}
              variant="outlined"
              onChange={handleChange}
            />
            {conditionTaskTypes && (
              <Box className="mt-24">
                {Object.values(conditionTaskTypes).map(taskType => (
                  <>
                    <Divider className="mb-24 mx-24" />
                    <AddConditionFormTaskType
                      isLoanEditor
                      conditionId={conditionId}
                      formName={FormNames.ADD_CONDITIONS_AND_CONDITIONS_SETS}
                      handleChange={handleChange}
                      key={taskType.taskTypeId}
                      setInForm={setInForm}
                      taskType={taskType}
                      taskTypeDynamicFieldOptions={taskTypeDynamicFieldOptions}
                      validateField={validateField}
                      validationErrors={validationErrors}
                      onCreateNewForm={onCreateNewForm}
                    />
                  </>
                ))}
              </Box>
            )}
          </Box>
        ),
      )}
    </ModalFormWrapper>
  );
};

AddConditionsForm.propTypes = {
  ...WithLoadingPropTypes,
  formRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
  customOnAssignFormClick: PropTypes.func,
};

export default compose(withLoading, memo)(AddConditionsForm);
